import React, { useState, useEffect } from 'react';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import supabase from '../supabase/supabase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { SearchableCountrySelect } from '../components/ui/SearchableCountrySelect';

const { FiMail, FiCheckCircle, FiGift, FiTag, FiLock, FiArrowLeft, FiRefreshCw } = FiIcons;

const Login = () => {
  const { user } = useStore();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetResendTimer, setResetResendTimer] = useState(0);
  const [verificationPending, setVerificationPending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [successMsg, setSuccessMsg] = useState('');
  const [showWelcomePromo, setShowWelcomePromo] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [gender, setGender] = useState('');
  const [countryCode, setCountryCode] = useState('+94');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/shop';

  useEffect(() => {
    if (user && !showForgotPassword) {
      navigate(redirect);
    }
  }, [user, navigate, redirect, showForgotPassword]);

  useEffect(() => {
    const hash = window.location.hash;
    let params = new URLSearchParams();

    if (hash && (hash.includes('access_token=') || hash.includes('error='))) {
       const authPart = hash.split(/#|\?/).find(part => part.includes('access_token=') || part.includes('error='));
       if(authPart) params = new URLSearchParams(authPart);
    } else {
       params = searchParams;
    }

    const err = params.get('error');
    const errCode = params.get('error_code');
    const errDesc = params.get('error_description');

    if (err) {
      if (errCode === 'otp_expired') {
        setError('The verification link expired or has already been used. If you have already verified, please try logging in below.');
      } else {
        setError(errDesc ? errDesc.replace(/\+/g, ' ') : 'Authentication error occurred.');
      }
      window.history.replaceState(null, '', window.location.pathname);
    } else if (params.get('access_token') || params.get('type') === 'signup') {
      setSuccessMsg('Account verified successfully! Please log in.');
      setShowWelcomePromo(true);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [searchParams]);

  // Timer for signup verification resend
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer(prev => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Timer for password reset resend (120 seconds)
  useEffect(() => {
    let interval;
    if (resetResendTimer > 0) {
      interval = setInterval(() => setResetResendTimer(prev => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resetResendTimer]);

  const handleResend = async () => {
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      if (error) throw error;
      
      const nextTimer = resendAttempts >= 1 ? 120 : 60;
      setResendTimer(nextTimer);
      setResendAttempts(prev => prev + 1);
      setSuccessMsg('Verification email resent successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const redirectUrl = window.location.hostname === 'localhost'
        ? `${window.location.origin}/reset-password`
        : 'https://ceylanthro.com/reset-password';

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      if (error) throw error;
      setResetEmailSent(true);
      setResetResendTimer(120);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendResetEmail = async () => {
    setLoading(true);
    setError('');
    try {
      const redirectUrl = window.location.hostname === 'localhost'
        ? `${window.location.origin}/reset-password`
        : 'https://ceylanthro.com/reset-password';

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      if (error) throw error;
      setResetResendTimer(120);
      setSuccessMsg('Password reset email resent successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      if (isSignUp) {
        const fullPhone = `${countryCode} ${phoneNumber}`;
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              gender: gender,
              phone_number: fullPhone
            }
          }
        });

        if (signUpError) {
          if (signUpError.message.toLowerCase().includes('already registered')) {
            setError('You have already signed up. Please request a verification email.');
            setVerificationPending(true);
            setLoading(false);
            return;
          }
          throw signUpError;
        }

        if (data?.user && data.user.identities && data.user.identities.length === 0) {
          setError('You have already signed up. Please request a verification email.');
          setVerificationPending(true);
          setLoading(false);
          return;
        }

        setVerificationPending(true);
        setResendTimer(60);
        setResendAttempts(1);
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          if (signInError.message.toLowerCase().includes('email not confirmed')) {
            setError('Email not confirmed. Please verify your account.');
            setVerificationPending(true);
          } else {
            throw signInError;
          }
        } else {
          navigate(redirect);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="bg-brand-sand min-h-screen pt-24 font-sans flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6 my-10">
          <div className="bg-white/80 backdrop-blur-xl p-8 md:p-12 rounded-[2rem] border border-white shadow-xl w-full max-w-md">
            <button
              onClick={() => { setShowForgotPassword(false); setResetEmailSent(false); setError(''); setSuccessMsg(''); setResetResendTimer(0); }}
              className="flex items-center gap-2 text-xs font-bold text-brand-darkSage/70 hover:text-brand-gold transition-colors mb-6"
            >
              <FiArrowLeft size={14} /> Back to Login
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiLock} className="w-8 h-8 text-brand-gold" />
              </div>
              <h1 className="text-3xl font-serif text-brand-darkSage mb-2">Reset Password</h1>
              <p className="text-brand-lightText text-sm">We'll send you a secure link to reset your password.</p>
            </div>

            {error && <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm mb-6 border border-red-100">{error}</div>}
            {successMsg && <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm mb-6 border border-green-100 flex items-center justify-center gap-2"><SafeIcon icon={FiCheckCircle} /> {successMsg}</div>}

            {resetEmailSent ? (
              <div className="text-center">
                <div className="bg-green-50 border border-green-100 rounded-2xl p-6 mb-6">
                  <SafeIcon icon={FiCheckCircle} className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="font-bold text-green-800 mb-2">Check Your Email</h3>
                  <p className="text-sm text-green-700 leading-relaxed">
                    We've sent a password reset link to<br />
                    <strong>{email}</strong>
                  </p>
                  <p className="text-xs text-green-600 mt-3">
                    The link will expire in <strong>15 minutes</strong>. If you don't see it, check your spam folder.
                  </p>
                </div>

                {/* Resend Timer Section */}
                <div className="bg-brand-sand/50 rounded-xl p-4 mb-6 border border-brand-darkSage/5">
                  <p className="text-xs text-brand-darkSage/70 mb-3">Didn't receive the email?</p>
                  {resetResendTimer > 0 ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2 text-brand-darkSage">
                        <SafeIcon icon={FiRefreshCw} size={14} className="animate-spin" />
                        <span className="text-sm font-bold">
                          Resend available in <span className="text-brand-gold font-mono">{formatTimer(resetResendTimer)}</span>
                        </span>
                      </div>
                      <div className="w-full bg-brand-darkSage/10 rounded-full h-1.5 mt-1">
                        <div
                          className="bg-brand-gold h-1.5 rounded-full transition-all duration-1000"
                          style={{ width: `${((120 - resetResendTimer) / 120) * 100}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleResendResetEmail}
                      disabled={loading}
                      className="flex items-center gap-2 mx-auto bg-brand-darkSage text-white px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all disabled:opacity-50"
                    >
                      <SafeIcon icon={FiRefreshCw} size={14} />
                      {loading ? 'Sending...' : 'Resend Reset Email'}
                    </button>
                  )}
                </div>

                <button
                  onClick={() => { setShowForgotPassword(false); setResetEmailSent(false); setSuccessMsg(''); setResetResendTimer(0); }}
                  className="w-full bg-brand-darkSage text-white py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Email Address</label>
                  <input 
                    required 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-brand-darkSage/20 rounded-xl px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                    placeholder="Enter your registered email"
                    autoFocus
                  />
                </div>
                <button 
                  disabled={loading}
                  type="submit" 
                  className="w-full bg-brand-darkSage text-white py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (verificationPending) {
    return (
      <div className="bg-brand-sand min-h-screen pt-24 font-sans flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6 my-10">
          <div className="bg-white/80 backdrop-blur-xl p-8 md:p-12 rounded-[2rem] border border-white shadow-xl w-full max-w-md text-center">
            <SafeIcon icon={FiMail} className="w-16 h-16 text-brand-gold mx-auto mb-6" />
            <h1 className="text-3xl font-serif text-brand-darkSage mb-4">Verify Your Email</h1>
            
            {error && <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm mb-6 border border-red-100">{error}</div>}
            {successMsg && <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm mb-6 border border-green-100 flex items-center justify-center gap-2"><SafeIcon icon={FiCheckCircle} /> {successMsg}</div>}

            <p className="text-brand-lightText text-sm mb-6 leading-relaxed">
              We have sent a verification email to your registered email address <br/><strong className="text-brand-darkSage text-base">{email}</strong>.<br/>Please verify your account to continue.
            </p>

            <div className="bg-brand-sand/50 p-4 rounded-xl border border-brand-darkSage/10 mb-8 text-xs text-brand-darkSage/80">
              <p>Didn't receive the email?</p>
              <p className="mt-1">Please check your <strong>Junk or Spam</strong> folder just in case.</p>
            </div>

            <button 
              onClick={handleResend}
              disabled={resendTimer > 0 || loading}
              className="w-full bg-brand-darkSage text-white py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all disabled:opacity-50"
            >
              {loading ? 'Sending...' : resendTimer > 0 ? `Resend Email in ${resendTimer}s` : 'Resend Verification Email'}
            </button>

            <button 
              onClick={() => {
                setVerificationPending(false);
                setIsSignUp(false);
                setError('');
                setSuccessMsg('');
              }}
              className="mt-6 text-xs text-brand-darkSage/70 hover:text-brand-gold font-bold transition-colors"
            >
              &larr; Back to Login
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-brand-sand min-h-screen pt-24 font-sans flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center p-6 my-10">
        <div className="bg-white/80 backdrop-blur-xl p-8 md:p-12 rounded-[2rem] border border-white shadow-xl w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-serif text-brand-darkSage mb-2">{isSignUp ? 'Create Account' : 'Welcome Back'}</h1>
            <p className="text-brand-lightText text-sm">Experience the finest authentic Ceylon wellness.</p>
          </div>

          {error && <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm mb-6 border border-red-100">{error}</div>}
          {successMsg && <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm mb-6 border border-green-100 flex items-center justify-center gap-2"><SafeIcon icon={FiCheckCircle} /> {successMsg}</div>}

          {showWelcomePromo && (
            <div className="bg-gradient-to-r from-brand-gold/10 to-brand-gold/5 border border-brand-gold/20 p-5 rounded-2xl mb-6 text-left">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-brand-gold/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiGift} className="text-brand-gold w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-brand-darkSage text-sm mb-1">Welcome Gift! 🎉</h3>
                  <p className="text-xs text-brand-lightText leading-relaxed mb-2">
                    As a new member, enjoy <strong className="text-brand-gold">10% off</strong> your first order with code:
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-brand-darkSage bg-white px-3 py-1.5 rounded-lg border border-brand-gold/30 text-sm">
                      WELCOME01
                    </span>
                    <SafeIcon icon={FiTag} className="text-brand-gold" size={14} />
                  </div>
                  <p className="text-[10px] text-brand-lightText/70 mt-2">
                    Valid for 1 month from account creation. Auto-applied at checkout.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleAuth} className="space-y-4">
            {isSignUp && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Full Name</label>
                  <input 
                    required 
                    type="text" 
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full border border-brand-darkSage/20 rounded-xl px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Gender</label>
                  <select 
                    required 
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full border border-brand-darkSage/20 rounded-xl px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Contact Number</label>
                  <div className="flex gap-2">
                    <SearchableCountrySelect 
                      value={countryCode} 
                      onChange={(e) => setCountryCode(e.target.value)} 
                    />
                    <input 
                      required 
                      type="tel" 
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="flex-1 border border-brand-darkSage/20 rounded-xl px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none"
                      placeholder="77 123 4567"
                    />
                  </div>
                </div>
              </>
            )}
            
            <div>
              <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Email</label>
              <input 
                required 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-brand-darkSage/20 rounded-xl px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
              />
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-brand-darkSage uppercase tracking-wider mb-1">Password</label>
              <input 
                required 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-brand-darkSage/20 rounded-xl px-4 py-3 bg-white/50 focus:ring-brand-gold outline-none" 
              />
              {!isSignUp && (
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setError(''); setSuccessMsg(''); }}
                  className="mt-2 text-xs text-brand-gold hover:text-brand-darkSage font-bold transition-colors"
                >
                  Forgot Password?
                </button>
              )}
            </div>

            <button 
              disabled={loading}
              type="submit" 
              className="w-full bg-brand-darkSage text-white py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all mt-4 disabled:opacity-50"
            >
              {loading ? 'Processing...' : (isSignUp ? 'Sign Up' : 'Sign In')}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-brand-darkSage/70">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            <button 
              type="button"
              onClick={() => setIsSignUp(!isSignUp)} 
              className="ml-2 font-bold text-brand-gold hover:underline"
            >
              {isSignUp ? 'Sign In' : 'Sign Up'}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;